/**
 * Add Note Dialog - adds a manual entry to a client's activity timeline.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StickyNote, Loader2, Upload, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
}

export function AddNoteDialog({ open, onOpenChange, clientId }: AddNoteDialogProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setAttachments([]);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const attachmentsPayload = await Promise.all(
        attachments.map(async (f) => ({
          filename: f.name,
          size: f.size,
          contentType: f.type,
          fileBase64: await fileToBase64(f),
        }))
      );
      const resp = await apiClient.post(`/clients/${clientId}/timeline/notes`, {
        title,
        description: description || undefined,
        metadata: attachmentsPayload.length ? { attachments: attachmentsPayload } : undefined,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: () => {
      toast.success('Note added');
      queryClient.invalidateQueries({ queryKey: ['client-timeline', clientId] });
      reset();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to add note');
    },
  });

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const canSubmit = !!title.trim() && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-amber-500" /> Add Note
          </DialogTitle>
          <DialogDescription>Log an interaction, observation, or follow-up task for this client.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-title">Title *</Label>
            <Input id="note-title" placeholder="e.g. Phone call with trustee" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="note-desc">Description</Label>
            <Textarea id="note-desc" rows={4} placeholder="Details…" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Attachments</Label>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border p-2 bg-muted/30 text-xs">
                    <FileText className="h-3.5 w-3.5 text-blue-500" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</span>
                    <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-md py-3 cursor-pointer hover:bg-muted/40 text-xs text-muted-foreground">
              <Upload className="h-4 w-4" /> Add files
              <input type="file" multiple className="hidden" onChange={handleFiles} />
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddNoteDialog;
